import { useState, useEffect } from 'react';
import { useRecruitmentStore, type CandidateStage } from '../../../store/recruitmentStore';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';
import { Pencil, Trash2 } from 'lucide-react';

const STAGES: CandidateStage[] = ['Applied', 'Screening', 'Technical', 'Manager', 'Offer', 'Hired'];

const stageColors: Record<CandidateStage | 'Rejected', string> = {
    Applied: 'border-text-400/30',
    Screening: 'border-info/40',
    Technical: 'border-primary/40',
    Manager: 'border-warning/40',
    Offer: 'border-success/40',
    Hired: 'border-success',
    Rejected: 'border-destructive/30',
};

const stageBg: Record<CandidateStage | 'Rejected', string> = {
    Applied: 'bg-card/30',
    Screening: 'bg-info/5',
    Technical: 'bg-primary/5',
    Manager: 'bg-warning/5',
    Offer: 'bg-success/5',
    Hired: 'bg-success/10',
    Rejected: 'bg-destructive/5',
};

const onboardingTasks: any[] = [];

const categoryColors: Record<string, string> = {
    documentation: 'bg-info/20 text-info',
    compliance: 'bg-warning/20 text-warning',
    equipment: 'bg-primary/20 text-primary',
    training: 'bg-success/20 text-success',
    meetings: 'bg-text-400/20 text-muted-foreground',
};

export function RecruitmentPipeline() {
    const {
        candidates, jobs, onboarding,
        fetchJobs, fetchCandidates, fetchOnboarding,
        updateCandidateStage, updateOnboardingTask,
        createJob, updateJob, deleteJob, deleteCandidate, isLoading
    } = useRecruitmentStore();

    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [activeView, setActiveView] = useState<'pipeline' | 'jobs' | 'onboarding'>('pipeline');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingJobId, setEditingJobId] = useState<string | null>(null);
    const EMPTY_JOB = {
        title: '', department: '', location: '',
        type: 'Full-time', priority: 'Medium', description: '', requirements: '',
    };
    const [newJob, setNewJob] = useState(EMPTY_JOB);
    const [deleteJobTarget, setDeleteJobTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleteCandTarget, setDeleteCandTarget] = useState<{ id: string; name: string } | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchJobs();
        fetchCandidates();
        fetchOnboarding();
    }, []);

    const openCreateJob = () => {
        setEditingJobId(null);
        setNewJob(EMPTY_JOB);
        setIsCreateModalOpen(true);
    };

    const openEditJob = (job: any) => {
        setEditingJobId(job.id);
        setNewJob({
            title: job.title ?? '',
            department: job.department ?? '',
            location: job.location ?? '',
            type: job.type ?? 'Full-time',
            priority: job.priority ?? 'Medium',
            description: job.description ?? '',
            requirements: job.requirements ?? '',
        });
        setIsCreateModalOpen(true);
    };

    const handleSaveJob = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editingJobId) {
                await updateJob(editingJobId, newJob);
                toast.success('Job opening updated');
            } else {
                await createJob(newJob);
                toast.success('Job opening created successfully!');
            }
            setIsCreateModalOpen(false);
            setEditingJobId(null);
            setNewJob(EMPTY_JOB);
        } catch (error) {
            toast.error(editingJobId ? 'Failed to update job opening' : 'Failed to create job opening');
        }
    };

    const handleDeleteJob = async () => {
        if (!deleteJobTarget) return;
        setBusy(true);
        try {
            await deleteJob(deleteJobTarget.id);
            toast.success('Job opening deleted');
            setDeleteJobTarget(null);
        } catch {
            toast.error('Failed to delete job opening');
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteCandidate = async () => {
        if (!deleteCandTarget) return;
        setBusy(true);
        try {
            await deleteCandidate(deleteCandTarget.id);
            toast.success('Candidate removed');
            setDeleteCandTarget(null);
        } catch {
            toast.error('Failed to remove candidate');
        } finally {
            setBusy(false);
        }
    };

    const handleTaskClick = async (taskId: string, currentStatus: string) => {
        const nextStatus = currentStatus === 'completed' ? 'pending' : 'completed';
        try {
            await updateOnboardingTask(taskId, nextStatus);
            toast.success(`Task marked as ${nextStatus}`);
        } catch (error) {
            toast.error('Failed to update task');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">
                        Recruitment <span className="text-primary">ATS</span>
                    </h1>
                    <p className="text-muted-foreground mt-1">Applicant Tracking System — Candidate Pipeline & Onboarding</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="primary" onClick={openCreateJob}>+ Create Job</Button>
                </div>
            </div>

            {/* Create Job Modal */}
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>{editingJobId ? 'Edit Job Opening' : 'Create Job Opening'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSaveJob} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Job Title</label>
                                    <Input 
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:border-primary outline-none transition-all"
                                        placeholder="e.g. Senior Backend Engineer"
                                        value={newJob.title}
                                        onChange={e => setNewJob({...newJob, title: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Department</label>
                                    <Input 
                                        required
                                        className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:border-primary outline-none transition-all"
                                        placeholder="e.g. Engineering"
                                        value={newJob.department}
                                        onChange={e => setNewJob({...newJob, department: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Location</label>
                                    <Input 
                                        className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:border-primary outline-none transition-all"
                                        placeholder="e.g. Remote / New York"
                                        value={newJob.location}
                                        onChange={e => setNewJob({...newJob, location: e.target.value})}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted-foreground">Type</label>
                                    <select 
                                        className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:border-primary outline-none transition-all"
                                        value={newJob.type}
                                        onChange={e => setNewJob({...newJob, type: e.target.value})}
                                    >
                                        <option value="Full-time">Full-time</option>
                                        <option value="Part-time">Part-time</option>
                                        <option value="Contract">Contract</option>
                                        <option value="Internship">Internship</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-muted-foreground">Description</label>
                                <textarea 
                                    className="w-full bg-background border border-border rounded-lg px-4 py-2.5 text-foreground focus:border-primary outline-none transition-all h-24 resize-none"
                                    placeholder="Briefly describe the role..."
                                    value={newJob.description}
                                    onChange={e => setNewJob({...newJob, description: e.target.value})}
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <Button type="button" variant="secondary" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
                                <Button type="submit" variant="primary" disabled={isLoading}>
                                    {editingJobId ? 'Save Changes' : (isLoading ? 'Creating...' : 'Create Opening')}
                                </Button>
                            </div>
                        </form>
                </DialogContent>
            </Dialog>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Open Positions', value: jobs.filter(j => j.status === 'Open').length, icon: '' },
                    { label: 'Total Candidates', value: candidates.length, icon: '' },
                    { label: 'In Interview Stage', value: candidates.filter(c => c.stage === 'Technical' || c.stage === 'Manager').length, icon: '' },
                    { label: 'Offers Extended', value: candidates.filter(c => c.stage === 'Offer').length, icon: '' },
                ].map((stat, i) => (
                    <div key={i} className="p-5 bg-card border border-border rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-xs text-muted-foreground font-semibold">{stat.label}</p>
                            <span className="text-lg">{stat.icon}</span>
                        </div>
                        <p className="text-foreground font-display text-metric">{stat.value}</p>
                    </div>
                ))}
            </div>

            {/* Tab Nav */}
            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'pipeline', label: 'Pipeline' },
                    { id: 'jobs', label: 'Job Openings' },
                    { id: 'onboarding', label: 'Onboarding' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled"
                        key={t.id}
                        onClick={() => setActiveView(t.id as typeof activeView)}
                        className={`pb-4 px-4 whitespace-nowrap text-sm font-semibold transition-all relative cursor-pointer ${activeView === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        {t.label}
                        {activeView === t.id && (
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                        )}
                    </Button>
                ))}
            </div>

            {/* ── Pipeline Kanban ── */}
            {activeView === 'pipeline' && (
                <div className="flex gap-4 overflow-x-auto pb-6" style={{ minHeight: '60vh' }}>
                    {STAGES.map(stage => {
                        const stageCandidates = candidates.filter(c => c.stage === stage);
                        return (
                            <div
                                key={stage}
                                className="flex-shrink-0 w-64 flex flex-col"
                                onDragOver={e => e.preventDefault()}
                                onDrop={() => {
                                    if (draggedId) {
                                        updateCandidateStage(draggedId, stage);
                                        toast.success(`Moved to ${stage}`);
                                        setDraggedId(null);
                                    }
                                }}
                            >
                                {/* Column header */}
                                <div className={`flex justify-between items-center px-3 py-2.5 mb-3 rounded-lg bg-card border ${stageColors[stage]}`}>
                                    <h3 className="text-foreground font-display text-card-title">{stage}</h3>
                                    <Badge variant={stageCandidates.length > 0 ? 'primary' : 'default'}>{stageCandidates.length}</Badge>
                                </div>

                                {/* Drop zone */}
                                <div
                                    className={`flex-1 p-3 rounded-lg border-2 border-dashed space-y-3 transition-all ${stageColors[stage]} ${stageBg[stage]}`}
                                    style={{ minHeight: 180 }}
                                >
                                    {stageCandidates.map(c => (
                                        <div
                                            key={c.id}
                                            draggable
                                            onDragStart={() => setDraggedId(c.id)}
                                            className="bg-card rounded-lg p-4 border border-border hover:border-primary/50 cursor-grab active:cursor-grabbing hover:-translate-y-1 transition-all duration-200 group select-none"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="w-9 h-9 rounded-lg flex items-center justify-center font-semibold text-primary text-sm">
                                                    {c.name[0]}
                                                </div>
                                                <span className="text-xs font-semibold text-warning">{c.rating}</span>
                                            </div>
                                            <h4 className="text-foreground leading-tight text-sm font-semibold">{c.name}</h4>
                                            <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{c.role}</p>
                                            <div className="mt-3 flex justify-between items-center">
                                                <span className="text-xs text-muted-foreground font-semibold">
                                                    {new Date(c.appliedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                </span>
                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="unstyled" size="unstyled"
                                                        onClick={() => setDeleteCandTarget({ id: c.id, name: c.name })}
                                                        aria-label={`Remove ${c.name}`}
                                                        className="text-xs px-2 py-0.5 bg-destructive/15 text-destructive rounded-lg font-semibold hover:bg-destructive/25 cursor-pointer inline-flex items-center gap-1"
                                                    >
                                                        <Trash2 className="w-3 h-3" /> Remove
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {stageCandidates.length === 0 && (
                                        <div className="flex items-center justify-center h-16 text-xs text-muted-foreground">
                                            Drop candidates here
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Job Openings ── */}
            {activeView === 'jobs' && (
                <Card>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                    <th className="text-left px-6 py-4">Position</th>
                                    <th className="text-left px-4 py-4">Department</th>
                                    <th className="text-center px-4 py-4">Status</th>
                                    <th className="text-center px-4 py-4">Candidates</th>
                                    <th className="text-center px-4 py-4">Priority</th>
                                    <th className="text-right px-6 py-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-12">
                                {jobs.length > 0 ? jobs.map((job) => (
                                    <tr key={job.id} className="hover:bg-card/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-foreground">{job.title}</p>
                                            <p className="text-xs text-muted-foreground">Posted: {job.posted ?? (job as any).posted_at ? new Date((job as any).posted_at).toLocaleDateString() : '—'}</p>
                                        </td>
                                        <td className="px-4 py-4 text-muted-foreground">{job.department}</td>
                                        <td className="px-4 py-4 text-center">
                                            <Badge variant={job.status === 'Open' ? 'success' : job.status === 'On-Hold' ? 'warning' : 'default'}>
                                                {job.status}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-4 text-center font-semibold text-foreground">{job.candidatesCount ?? 0}</td>
                                        <td className="px-4 py-4 text-center">
                                            <Badge variant={job.priority === 'Urgent' ? 'error' : job.priority === 'High' ? 'warning' : 'default'}>
                                                {job.priority ?? 'Low'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="ghost" size="icon-sm" aria-label="Edit job"
                                                    className="text-muted-foreground hover:text-foreground"
                                                    onClick={() => openEditJob(job)}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon-sm" aria-label="Delete job"
                                                    className="text-muted-foreground hover:text-destructive"
                                                    onClick={() => setDeleteJobTarget({ id: job.id, title: job.title })}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                                <Button variant="unstyled" size="unstyled"
                                                    className="text-sm text-primary hover:text-primary font-semibold cursor-pointer ml-1"
                                                    onClick={() => setActiveView('pipeline')}
                                                >
                                                    View →
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No job openings found. Click "+ Create Job" to add one.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            {/* ── Onboarding ── */}
            {activeView === 'onboarding' && (
                <div className="space-y-6">
                    {onboarding.length > 0 ? onboarding.map((process) => (
                        <div key={process.id} className="space-y-4">
                            <div className="p-5 bg-success/10 border border-success/30 rounded-lg flex items-center gap-4">
                                <span className="text-3xl"></span>
                                <div>
                                    <p className="font-semibold text-foreground">{process.first_name} {process.last_name} — {process.job_title}</p>
                                    <p className="text-sm text-muted-foreground">Onboarding Status: {process.status}</p>
                                </div>
                                <div className="ml-auto text-right shrink-0">
                                    <p className="text-sm font-semibold text-success">
                                        {process.tasks?.filter(t => t.status === 'completed').length || 0} / {process.tasks?.length || 0} tasks done
                                    </p>
                                    <div className="w-32 h-1.5 bg-card rounded-full overflow-hidden mt-1">
                                        <div 
                                            className="h-full bg-success rounded-full" 
                                            style={{ width: `${((process.tasks?.filter(t => t.status === 'completed').length || 0) / (process.tasks?.length || 1)) * 100}%` }} 
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {['Pre-Boarding', 'Day 1', 'Week 1', 'Month 1'].map((phase) => {
                                    const phaseTasks = process.tasks?.filter(t => t.phase === phase) || [];
                                    if (phaseTasks.length === 0) return null;
                                    return (
                                        <Card key={phase}>
                                            <div className="px-6 py-4 bg-card/30 border-b border-border rounded-t-2xl">
                                                <h3 className="text-foreground font-display text-card-title">{phase}</h3>
                                            </div>
                                            <CardContent className="p-4 space-y-3">
                                                {phaseTasks.map((task) => (
                                                    <div key={task.id} className="flex items-center gap-3 p-3 bg-card/30 rounded-lg">
                                                        <div
                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 cursor-pointer ${task.status === 'completed'
                                                                    ? 'border-success bg-success/20'
                                                                    : task.status === 'in-progress'
                                                                        ? 'border-warning bg-warning/20'
                                                                        : 'border-border'
                                                                }`}
                                                            onClick={() => handleTaskClick(task.id, task.status)}
                                                        >
                                                            {task.status === 'completed' && <span className="text-xs text-success font-semibold"></span>}
                                                            {task.status === 'in-progress' && <span className="w-2 h-2 rounded-full bg-warning block" />}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p
                                                                className={`text-sm font-medium ${task.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'
                                                                    }`}
                                                            >
                                                                {task.title}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">{task.assignee_type}</p>
                                                        </div>
                                                        <span
                                                            className={`text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 ${categoryColors[task.category] || 'bg-text-400/20 text-muted-foreground'}`}
                                                        >
                                                            {task.category}
                                                        </span>
                                                    </div>
                                                ))}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                    )) : (
                        <div className="text-center py-12 text-muted-foreground">No active onboarding processes. Recruit and hire candidates to start.</div>
                    )}
                </div>
            )}

            {/* Delete job confirmation */}
            <Dialog open={!!deleteJobTarget} onOpenChange={(o) => !o && setDeleteJobTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete job opening</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteJobTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteJobTarget(null)} disabled={busy}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteJob} isLoading={busy}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete candidate confirmation */}
            <Dialog open={!!deleteCandTarget} onOpenChange={(o) => !o && setDeleteCandTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Remove candidate</DialogTitle>
                        <DialogDescription>
                            Remove <span className="font-medium text-foreground">{deleteCandTarget?.name}</span> from the pipeline? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteCandTarget(null)} disabled={busy}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteCandidate} isLoading={busy}>Remove</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


